import React, { FC, ReactNode, useRef, useState, useEffect } from 'react';
import classNames from 'classnames/bind';

import { connect } from 'react-redux';
import Slider from 'react-slick';
import styles from './index.pcss';
import luxeStyles from './luxeStyles.pcss';
import luxeScreen from '../../constants/luxeScreenType';
import {
  gtmListingProductImpressions,
  luxeGtmProductImpressions,
  getStoreName
} from '../../../modules/ga/GTMdatalayer';
import { getCurrentStore } from '../../selectors/metaData';
import { getDiscountValue } from '../../../utils/price';
import { checkProductId, setProductIdList, setProductId } from '../../../utils/productWidget';
import { handleProductImpressionsForDynamicWidget } from '../../../fleek/utils/feedGaUtils';
import { getGiftWithProductEnable } from '../../selectors/featureFlagsSelector';

const cx = classNames.bind(styles);
const luxecx = classNames.bind(luxeStyles);

const SEGMENT_BATCH_PREFIX = 'segment_pdp_widget_batch';
const SEGMENT_SEEN_PREFIX = 'segment_pdp_widget_seen';
const SEGMENT_BATCH_SIZE = 10;

type Item = {
  id?: string | number;
  source: string;
  description: string;
};

const getUrlPathname = (url: string): string => {
  try {
    return new URL(url, 'https://www.ajio.com').pathname;
  } catch {
    return url || '';
  }
};

const getSlideUrl = (slide: any): string =>
  slide?.slideItem || slide?.source || slide?.url || '';

const resolveLcpForSlide = (
  pageType: string | undefined,
  index: number,
  slide: any
): HTMLElement | null => {
  if (pageType !== 'pdp' || index !== 0) return null;
  if (typeof document === 'undefined') return null;
  const lcpEl = document.getElementById('LCP_image');
  if (!lcpEl) return null;
  const ssrSrc = (lcpEl as HTMLImageElement).src;
  const slideUrl = getSlideUrl(slide);
  if (!ssrSrc || !slideUrl) return null;
  if (getUrlPathname(ssrSrc) !== getUrlPathname(slideUrl)) return null;
  return lcpEl;
};

const renderSlides: (
  slides: Array<Partial<Item>>,
  slideRenderer: (item: any, idx: number, element?: HTMLElement | null) => ReactNode,
  pageType?: string
) => ReactNode[] = (slides, slideRenderer, pageType) => {
  if (!Array.isArray(slides)) return [];
  return slides.map((item, idx) =>
    slideRenderer(item, idx, resolveLcpForSlide(pageType, idx, item))
  );
};

const renderSlidesInDiv: (
  slides: Array<Partial<Item>>,
  slideRenderer: (item: any, idx: number, element?: HTMLElement | null) => ReactNode,
  pageType?: string
) => ReactNode[] = (slides, slideRenderer, pageType) => {
  if (!Array.isArray(slides)) return [];
  return slides.map((item, idx) => (
    <div key={idx}>{slideRenderer(item, idx, resolveLcpForSlide(pageType, idx, item))}</div>
  ));
};


interface HorizontalScrollableProps {
  items: Array<Partial<Item>>;
  renderItem: (item: any, idx: number) => ReactNode | FC;
  containerClassNames?: string;
  itemContainerClassNames?: string;
  hideScroll?: boolean;
  luxe?: boolean | undefined;
  pageType?: string;
  pageList?: string;
  currentStore?: string;
  isPDPScroll?: string;
  productWidgetCard?: boolean;
  similarProductsVariant?: boolean;
  productLiked?: boolean;
  widgetTitle?: string;
  isDynamicProductList?: boolean;
  heading: string | undefined;
  rowNumber: Number;
  banners: any;
  enableGiftWithProduct?: boolean;
  exitIntent?: boolean;
  isCrossSell? : boolean;
  baseProduct? : object;
  showVideoIcon? : () => void;
  videoIndex? : Number;
  setVideoIndex? : () => void;
  pdpVideosEnabled? : boolean;
  isRoleToContainer?:boolean
  parentProduct? : object;
}

const HorizontalScrollable: FC<HorizontalScrollableProps> = (props: HorizontalScrollableProps) => {
  const {
    items,
    renderItem,
    containerClassNames,
    itemContainerClassNames,
    hideScroll,
    luxe,
    pageType,
    pageList,
    currentStore,
    isPDPScroll,
    productWidgetCard,
    similarProductsVariant,
    productLiked,
    widgetTitle,
    isDynamicProductList,
    banners,
    heading,
    rowNumber,
    exitIntent,
    isCrossSell,
    baseProduct,
    showVideoIcon,
    videoIndex = 0,
    setVideoIndex,
    pdpVideosEnabled = false,
    isRoleToContainer = false,
    parentProduct,
    isSearchPage = false,
    parentProductObj={}
  } = props;
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const sliderContainerRef = useRef(null);

  const dynamicWidgetProductImpressionsIdMsite = 'dynamicWidgetProductImpressionsIdMsite';
  const dynamicWidgetProductImpressionsDataMsite = 'dynamicWidgetProductImpressionsDataMsite';
  const containerCls =
    luxe && pageType === 'pdp'
      ? luxecx('container', {
          [`${containerClassNames}`]: !!containerClassNames,
          'no-scroll': !!hideScroll
        })
      : cx(luxe ? 'luxeContainer' : 'container',pageType === 'pdp' && 'pdp-container', (luxe && pageType !== 'pdp-msite') ? 'product-list-left-padding' : '', {
          [`${containerClassNames}`]: !!containerClassNames,
          'no-scroll': !!hideScroll
        });

  const scrollCls = cx('horizontal-swipe', {
    [`${itemContainerClassNames}`]: !!itemContainerClassNames,
    'no-scroll': !!hideScroll
  });
  const [viewedProducts, setViewedProducts] = useState([]);
  const [viewedOldProducts, setViewedOldProducts] = useState([]);
  const [visibleVideo, setVisibleVideo] = useState(null);
  const valueRef = useRef();
  const productWidgetDataListLocalVariable = pageType === 'bag screen' ? 'oosShowSimilar' : 'productWidgetDataListMsite';
  const exitIntentproductWidgetDataList = 'exitIntentProductWidgetList';

  useEffect(() => {
    if (pdpVideosEnabled && containerRef && containerRef.current) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target.tagName === "VIDEO") {
            if (entry.isIntersecting) {
              setVisibleVideo(entry.target);
              showVideoIcon(false);
            } else if (visibleVideo === entry.target) {
              showVideoIcon(true);
              setVisibleVideo(null);
            }
          }
        });
      },
      {
        root: containerRef.current,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    const videos = containerRef.current.querySelectorAll("video");
    videos.forEach((video) => observer.observe(video));

    return () => {
      videos.forEach((video) => observer.unobserve(video));
    };
  }
  return;
  }, [visibleVideo]);

  useEffect(() => {
    if (pdpVideosEnabled && sliderContainerRef.current && videoIndex > 0) {
      const scrollPosition = videoIndex * window.innerWidth;
      sliderContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
      setVideoIndex(0);
    }
  }, [videoIndex]);

  useEffect(() => {
    if (pdpVideosEnabled && sliderRef.current && videoIndex > 0) {
      sliderRef.current.slickGoTo(videoIndex);
      setVideoIndex(0);
    }
  }, [videoIndex]);
  // useEffect(() => {
  //   const pdpSections = document.querySelectorAll('section[class*="pdp-container_"]');

  //   if (pdpSections.length > 0 && LCP_image) {
  //     pdpSections.forEach(section => {
  //       if (imgContainerRef.current && section.contains(imgContainerRef.current)) {
  //         imgContainerRef.current.prepend(LCP_image);
  //       }
  //     });
  //   }
  // }, []);

  useEffect(() => {
    if (
      pageType === 'pdp-msite' ||
      pageType === 'landing screen' ||
      pageType === 'pdpExitIntent' ||
      widgetTitle?.toLowerCase() === 'recently'
    ) {
      window.addEventListener('beforeunload', sendRemainingImpressions);
      luxeSetDefaultProductImpression();
    }
    if(sliderContainerRef && isRoleToContainer){
      const slickTrack = sliderContainerRef.current?.querySelector('.slick-track');
      if (slickTrack) {
        slickTrack.setAttribute('role', 'list');
      } else if (!slickTrack && isRoleToContainer && luxe) {
        sliderContainerRef.current?.setAttribute('role', 'list');
      }
      if(luxe){
        const tempSlides = sliderContainerRef.current?.querySelectorAll('.slick-slide');
        tempSlides.forEach((slide) => {
          slide.setAttribute('role', 'listitem');
        });
      }

    }
    
    return () => {
      if (pageType === 'pdp-msite' || pageType === 'landing screen' || pageType === 'pdpExitIntent' || pageType === 'bag screen' || widgetTitle?.toLowerCase() === 'recently') {
        window.removeEventListener('beforeunload', sendRemainingImpressions);
      }
    };
  }, []);

  useEffect(() => {
    if (pageType === 'pdp-msite' || pageType === 'landing screen') {
      luxeSetDefaultProductImpression();
    }
    if (similarProductsVariant && pageType == 'plp') {
      productImpressionsEventPush(parentProduct,items);
    }
  }, [items, similarProductsVariant]);


  const sendRemainingImpressions = () => {
    if (widgetTitle) {
      const batchKey = `${SEGMENT_BATCH_PREFIX}_${widgetTitle}`;
      const seenKey = `${SEGMENT_SEEN_PREFIX}_${widgetTitle}`;
      let batch: any[];
      try {
        batch = JSON.parse(sessionStorage.getItem(batchKey) || '[]');
      } catch {
        batch = [];
      }
      if (batch.length > 0) {
        productImpressionsEventPush(parentProduct, batch, true);
        sessionStorage.removeItem(batchKey);
      }
      sessionStorage.removeItem(seenKey);
    } else {
      productImpressionsEventPush(parentProduct, valueRef.current);
    }
  };

  const luxeSetDefaultProductImpression = () => {
    if (items && items.length > 0 && !exitIntent) {
      let products = items.slice(0, 2);
      setViewedProducts(products);
      setViewedOldProducts(products);
      setProductIdList(products, productWidgetDataListLocalVariable);
      valueRef.current = products;
    }
  };

  useEffect(() => {
    const isTrackedPageType =
      pageType === 'pdp-msite' ||
      pageType === 'landing screen' ||
      pageType === 'bag screen' ||
      pageType === 'pdpExitIntent';

    if (!isTrackedPageType || !items?.length) return;

    const exitIntentBatch: any[] = [];

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const index = Number((entry.target as HTMLElement).dataset.productIndex);
          const product = items[index];
          if (!product) return;

          const productCode = String((product as any)?.code || (product as any)?.productColor || '');

          if (productWidgetCard || !exitIntent) {
            const batchKey = `${SEGMENT_BATCH_PREFIX}_${widgetTitle}`;
            const seenKey = `${SEGMENT_SEEN_PREFIX}_${widgetTitle}`;
            let seenCodes: string[];
            try { seenCodes = JSON.parse(sessionStorage.getItem(seenKey) || '[]'); } catch { seenCodes = []; }

            if (productCode && !seenCodes.includes(productCode)) {
              seenCodes.push(productCode);
              sessionStorage.setItem(seenKey, JSON.stringify(seenCodes));
              let batch: any[];
              try { batch = JSON.parse(sessionStorage.getItem(batchKey) || '[]'); } catch { batch = []; }
              batch.push(product);
              sessionStorage.setItem(batchKey, JSON.stringify(batch));
              if (batch.length >= SEGMENT_BATCH_SIZE) {
                const productsToFlush = batch.splice(0, SEGMENT_BATCH_SIZE);
                productImpressionsEventPush(parentProduct, productsToFlush, true);
                sessionStorage.setItem(batchKey, JSON.stringify(batch));
              }
            }
          } else if (exitIntent) {
            (product as any).position = index;
            exitIntentBatch.push(product);
            if (exitIntentBatch.length === 5) {
              const toFlush = exitIntentBatch.splice(0, 5);
              productImpressionsEventPush(parentProduct, toFlush, true);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    items.forEach((product, index) => {
      const productCardId = widgetTitle
        ? exitIntent
          ? `exitIntent_product_${index}`
          : `product_${widgetTitle}_${index}`
        : `product_${index}`;
      const el = document.getElementById(productCardId);
      if (el) {
        el.dataset.productIndex = String(index);
        observer.observe(el);
      }
    });

    valueRef.current = exitIntentBatch;

    return () => {
      // Flush remaining on unmount (SPA page navigation)
      if (widgetTitle) {
        const batchKey = `${SEGMENT_BATCH_PREFIX}_${widgetTitle}`;
        const seenKey = `${SEGMENT_SEEN_PREFIX}_${widgetTitle}`;
        let batch: any[];
        try {
          batch = JSON.parse(sessionStorage.getItem(batchKey) || '[]');
        } catch {
          batch = [];
        }
        if (batch.length > 0) {
          productImpressionsEventPush(parentProduct, batch, true);
          sessionStorage.removeItem(batchKey);
        }
        sessionStorage.removeItem(seenKey);
      }
      observer.disconnect();
    };
  }, [items, pageType, widgetTitle, exitIntent, productWidgetCard]);

  const productImpressionsEventPush = (parentProduct : Any, product: Array, skipDedupCheck = false): void => {
    let impressions = [];
    const {
      pdp: { screenName = '', screenType = '' },
      landing
    } = luxeScreen;
    const { enableGiftWithProduct = false } = props;
    product?.map((data, index) => {
      const {
        description,
        brand,
        code = '',
        priceValue,
        productColor,
        productCategory,
        brickName,
        productSize,
        discount = '',
        isExclusive,
        productTag,
        productTagEngineValue,
        productPriceLabel,
        segmentId = '',
        experimentId = '',
        offerPrice: { giftAvailable = false } = {},
        position = 0,
        uclid='',
        sclid='',
        sellerId=''
      } = data;
      let productId = code && code.length > 9 && !exitIntent && pageType !== 'bag screen' ? code.substring(0, 9) : code;
      let tag = isExclusive ? 'Exclusive' : productTag;
      let listValue = pageList;
      if (isDynamicProductList) {
        listValue =
          banners.widgetLevel.toLowerCase() === 'user'
            ? `user_level_${banners?.feedLogic}_${heading}_${rowNumber}`
            : `dynamic_${banners?.feedLogic}_${heading}_${rowNumber}`;
      }
      if (exitIntent) {
        listValue = 'pdp screen - explore similar overlay';
      }
      const dimension60Data =
        Number(segmentId) || Number(experimentId) ? `${segmentId}|${experimentId}` : '';
      let impression = {
        name: description,
        id: code && code.length > 9 && exitIntent ? code.substring(0, 9) : productId,
        price: priceValue,
        brand,
        category: productCategory,
        variant: productColor ? productColor.split('_')[1] : '',
        list: listValue, // example only,
        position: exitIntent ? position : index,
        dimension6: description, // pass value for product_name
        dimension7: productId, // pass value for product_id
        dimension8: brand, // pass value for product_brand
        dimension9: priceValue, // pass value for product_price
        dimension10: productColor ? productColor.split('_')[1] : '', // pass value for product_color
        dimension11: productSize, // pass value for product_size
        dimension12: discount, // pass value for product_trade_discount
        dimension13: productCategory || '', // pass value for product_category
        dimension14: brickName, // pass value for product_brick
        dimension15: tag, // pass value for product_tag
        dimension16: currentStore, // pass value for Store_type
        dimension23: getStoreName(),
        dimension60: dimension60Data,
        productTagEngineValue: productTagEngineValue || '',
        productPriceLabel: productPriceLabel || '',
        product_trade_discount: discount,
        adsFlag: {
          config: {
            sclid: sclid || '',
            ad_source: parentProductObj?.ad_source || '',
            cid: parentProductObj?.client_id || '',
            uclid: uclid || '',
          },
        },
        sellerId: sellerId || '',
        code: code || '',
      };

      if (enableGiftWithProduct && giftAvailable) {
        if (luxe) {
          impression.gwpDimension = 'GetFreebies';
        } else {
          impression.productPriceLabel = impression.productPriceLabel
            ? `${impression.productPriceLabel}, GetFreebies`
            : 'GetFreebies';
        }
      }
    const isProductIdExists = skipDedupCheck ? false : checkProductId(
        productId,
        exitIntent ? exitIntentproductWidgetDataList : productWidgetDataListLocalVariable
      );
      if (!isProductIdExists || isDynamicProductList) {
        impressions.push(impression);
        setProductId(
          productId,
          exitIntent ? exitIntentproductWidgetDataList : productWidgetDataListLocalVariable
        );
      }
    });
    if (impressions.length > 0) {
      if (luxe) {
        let luxeImpressions = [...impressions];
        luxeImpressions?.map(res => {
          res.dimension89 = res.productTagEngineValue;
          res.dimension90 = res.productPriceLabel;
          res.dimension91 = '';
          res.dimension82 = res.gwpDimension;
          delete res.gwpDimension;
          delete res.productTagEngineValue;
          delete res.productPriceLabel;
          delete res.product_trade_discount;
          return res;
        });
        if (isDynamicProductList) {
          luxeImpressions.forEach(imprsn => {
            handleProductImpressionsForDynamicWidget(
              dynamicWidgetProductImpressionsDataMsite,
              dynamicWidgetProductImpressionsIdMsite,
              imprsn,
              getStoreName(),
              productWidgetCard ? landing.screenName : screenName,
              productWidgetCard ? landing.screenType : screenType,
              '',
              !!luxe,
              true,
              impressions
            );
          });
        } else {
          luxeGtmProductImpressions({
            impressions: [...luxeImpressions],
            // list: productWidgetCard ? landing.screenName : pageList,
            store_type: currentStore,
            page_name: productWidgetCard ? landing.screenName : screenName,
            page_type: productWidgetCard ? landing.screenType : screenType,
            contains_luxe: !!luxe,
            ProductArray: impressions,                       
            parentProduct: parentProduct,
          });
        }
      } else {
        let ajioImpressions = [...impressions];
        ajioImpressions?.map(res => {
          res.dimension165 = res.productTagEngineValue;
          res.dimension166 = res.productPriceLabel;
          res.dimension167 = '';
          res.dimension170 = getDiscountValue(res.product_trade_discount);
          delete res.productTagEngineValue;
          delete res.productPriceLabel;
          delete res.product_trade_discount;
          return res;
        });
        if (isDynamicProductList) {
          ajioImpressions.forEach(imprsn => {
            handleProductImpressionsForDynamicWidget(
              dynamicWidgetProductImpressionsDataMsite,
              dynamicWidgetProductImpressionsIdMsite,
              imprsn,
              getStoreName(),
              isSearchPage ? 'search screen' : productWidgetCard ? landing.screenName : screenName,
              isSearchPage ? 'search screen' : productWidgetCard ? landing.screenType : screenType,
              '',
              !!luxe
            );
          });
        } else if (exitIntent) {
          gtmListingProductImpressions([...ajioImpressions], '','','','','','', parentProduct);
        } else {
          gtmListingProductImpressions({
            impressions: [...ajioImpressions],
            // list: productWidgetCard ? landing.screenName : pageList,
            store_type: getStoreName(),
            page_name: productWidgetCard ? landing.screenName : screenName,
            page_type: productWidgetCard ? landing.screenType : screenType,
            contains_luxe: !!luxe,            
          },'','','','','','',parentProduct,'HorizontalScrollable','Horizontal', '');
        }
      }
    }
  };

  let slickSettings = {
    dots: true,
    infinite: true,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    touchThreshold: 100
  };
  return luxe && pageType === 'pdp' && isPDPScroll ? (
    <section className={containerCls} id="slider-images" ref={sliderContainerRef} {...(isRoleToContainer && {'role':'list'})}>
      <Slider {...slickSettings} ref={sliderRef} style={{ minHeight: '400px' }}>
        {renderSlidesInDiv(items, renderItem, pageType)}
      </Slider>
    </section>
  ) : (
    <section
      className={containerCls}
      {...(isRoleToContainer && {'role':'list'})}
      ref={sliderContainerRef}
    >
      {renderSlides(items, renderItem, pageType)}
    </section>
  );
};

HorizontalScrollable.defaultProps = {
  containerClassNames: undefined,
  itemContainerClassNames: undefined,
  hideScroll: false
};

const mapStateToProps = (state, ownProps) => {
  return {
    currentStore: getCurrentStore(state),
    enableGiftWithProduct: getGiftWithProductEnable(state?.flags)
  };
};
export default connect(mapStateToProps, null)(HorizontalScrollable);
